/**
 * NotificationBell — global bell for all workspaces (payments, invitations,
 * member changes, plan-limit warnings, etc.).
 *
 * - Polls `/api/notifications` on mount + when opened
 * - Subscribes to `/api/ws` and increments the badge live on {type:'notification'}
 * - Never fails silently: any error just hides the badge (bell stays clickable)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { API, wsUrl } from '../utils/api';
import { useAuthContext } from '../context/AuthContext';

// Module-level cache keyed by user id. The bell can be re-mounted several
// times immediately after login (Gate→OwnerDashboard route change + StrictMode
// double-mount), which discards local state. Storing counts here means the
// surviving mount initialises with the last-known value instead of 0.
// Keyed by user id so switching identities does NOT bleed unread/items across
// accounts.
const _cache = new Map(); // userId -> { unread, items }
const _subscribers = new Set();
function _getEntry(uid) {
  if (!uid) return { unread: 0, items: [] };
  let e = _cache.get(uid);
  if (!e) { e = { unread: 0, items: [] }; _cache.set(uid, e); }
  return e;
}
function _notifySubscribers() { for (const cb of _subscribers) { try { cb(); } catch (_) { /* noop */ } } }
export function clearNotificationCache() { _cache.clear(); _notifySubscribers(); }

if (typeof window !== 'undefined') {
  window.addEventListener('paymig:auth-cleared', () => clearNotificationCache());
}

const RELATIVE_UNITS = [
  { limit: 60, div: 1, unit: 's' },
  { limit: 3600, div: 60, unit: 'm' },
  { limit: 86400, div: 3600, unit: 'h' },
  { limit: 604800, div: 86400, unit: 'd' },
];
function relTime(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 5) return 'now';
  for (const { limit, div, unit } of RELATIVE_UNITS) {
    if (diff < limit) return `${Math.floor(diff / div)}${unit}`;
  }
  return new Date(iso).toLocaleDateString();
}

export function NotificationBell({ variant = 'light' }) {
  const auth = useAuthContext();
  // Two useAuth() instances exist (App + Gate). Fall back to localStorage so
  // the bell keeps working after in-app SPA navigations (see WorkspacePage).
  const token = auth?.token || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
  const user = auth?.user || (() => {
    try { return typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || 'null') : null; } catch { return null; }
  })();
  const [open, setOpen] = useState(false);
  const uid = user?.id || user?._id || null;
  const initialEntry = _getEntry(uid);
  const [items, setItems] = useState(initialEntry.items);
  const [unread, setUnread] = useState(initialEntry.unread);
  const wsRef = useRef(null);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  // Subscribe to the module cache so cross-mount fetch results still update us.
  useEffect(() => {
    const sync = () => {
      const e = _getEntry(uid);
      setUnread(e.unread);
      setItems(e.items);
    };
    _subscribers.add(sync);
    sync();
    return () => { _subscribers.delete(sync); };
  }, [uid]);

  // (mount/unmount debug logs intentionally removed after diagnosis)

  // Load unread count on token change (and poll every 30s as a safety net).
  // Writes to the module cache so mount thrash right after login still
  // reaches every live instance via `_subscribers`.
  useEffect(() => {
    if (!token || !uid) return undefined;
    const fetchCount = async () => {
      try {
        const r = await axios.get(`${API}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const e = _getEntry(uid);
        e.unread = Number(r?.data?.count || 0);
        _notifySubscribers();
      } catch (_) { /* ignore */ }
    };
    fetchCount();
    const iv = setInterval(fetchCount, 30000);
    return () => { clearInterval(iv); };
  }, [token, uid]);

  const loadList = useCallback(async () => {
    if (!token || !uid) return;
    try {
      const r = await axios.get(`${API}/notifications?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const e = _getEntry(uid);
      e.items = r.data?.items || [];
      _notifySubscribers();
    } catch (_) { /* ignore */ }
  }, [token, uid]);

  useEffect(() => { /* list lazily loaded on open */ }, []);

  // Real-time push via WebSocket
  useEffect(() => {
    if (!user?.id) return;
    const url = wsUrl();
    if (!url) return;
    let closed = false;
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (_) { return; }
    wsRef.current = ws;
    ws.onopen = () => {
      try { ws.send(JSON.stringify({ type: 'subscribe', user_id: user.id })); } catch (_) { /* noop */ }
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === 'notification' ||
            msg.type === 'payment_received' ||
            msg.type === 'payment_request_received' ||
            msg.type === 'request_created') {
          const e = _getEntry(uid);
          e.unread += 1;
          _notifySubscribers();
          if (open) loadList();
        }
      } catch (_) { /* ignore */ }
    };
    return () => {
      closed = true;
      try { ws.close(); } catch (_) { /* noop */ }
      if (closed) wsRef.current = null;
    };
  }, [user?.id, open, loadList]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popRef.current && !popRef.current.contains(e.target) &&
          btnRef.current && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const markRead = async (id) => {
    const e = _getEntry(uid);
    e.items = e.items.map((x) => x.id === id ? { ...x, read: true } : x);
    e.unread = Math.max(0, e.unread - 1);
    _notifySubscribers();
    try {
      await axios.post(`${API}/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (_) { /* keep optimistic */ }
  };
  const markAll = async () => {
    const e = _getEntry(uid);
    e.items = e.items.map((x) => ({ ...x, read: true }));
    e.unread = 0;
    _notifySubscribers();
    try { await axios.post(`${API}/notifications/read-all`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    }); } catch (_) { /* noop */ }
  };

  if (!token || !user) return null;

  const dark = variant === 'dark';

  return (
    <div className="relative">
      <button
        ref={btnRef}
        data-testid="notif-bell-btn"
        data-unread={unread}
        onClick={toggle}
        aria-label="Notifications"
        className={`relative p-2 rounded-full transition-colors ${
          dark ? 'hover:bg-slate-800 text-slate-300' : 'hover:bg-teal-50 text-slate-600'
        }`}
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notif-bell-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-rose-500 rounded-full leading-none"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={popRef}
          data-testid="notif-bell-panel"
          className="absolute right-0 mt-2 w-[340px] max-w-[92vw] bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="text-[13px] font-semibold text-slate-800">Notifications</div>
            {items.some((x) => !x.read) && (
              <button
                data-testid="notif-mark-all"
                onClick={markAll}
                className="text-[11.5px] text-teal-700 font-medium inline-flex items-center gap-1 hover:underline"
              >
                <CheckCheck size={13} />Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-10 text-center text-[12.5px] text-slate-400" data-testid="notif-empty">
                No notifications yet
              </div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                data-testid={`notif-item-${n.id}`}
                onClick={() => !n.read && markRead(n.id)}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors ${
                  n.read ? 'bg-white' : 'bg-teal-50/40'
                }`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <div className={`text-[12.5px] font-semibold truncate ${n.read ? 'text-slate-600' : 'text-slate-900'}`}>
                        {n.title || n.type}
                      </div>
                      <div className="text-[10.5px] text-slate-400 shrink-0">{relTime(n.created_at)}</div>
                    </div>
                    <div className={`text-[12px] mt-0.5 ${n.read ? 'text-slate-400' : 'text-slate-600'}`}>
                      {n.message}
                    </div>
                  </div>
                  {n.read && <Check size={12} className="text-slate-300 mt-1" />}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
